import * as React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  FieldHint,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from "@repo/ui";
import {
  sanitizeNDCInput,
  formatNDC,
  isValidNDC,
  isNDCLookupLength,
} from "@repo/utils";
import { Plus, Save, Loader2, Trash2, Pill } from "lucide-react";
import { useNDCLookup, type NDCLookupResult } from "@/hooks/use-ndc-lookup";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { formatDrugSchedule } from "@/lib/utils/inventory-format";
import { bulkCreateInventory } from "@/lib/api/inventory";
import { ApiClientError } from "@/lib/api/client";

interface InventoryItem {
  id?: string;
  ndc: string;
  item_name: string;
  drug_type?:
    | "schedule_ii"
    | "schedule_iii"
    | "schedule_iv"
    | "schedule_v"
    | null;
  initial_stock: number;
  reorder_threshold: number;
  is_active?: boolean;
}

interface EditableInventoryTableProps {
  onBulkSaveSuccess?: () => void;
  onCancel?: () => void;
}

function validateInventoryItem(item: InventoryItem): string[] {
  const errors: string[] = [];

  if (!item.ndc) {
    errors.push("NDC is required");
  } else if (!isValidNDC(item.ndc)) {
    errors.push("NDC must be resolved to format: XXXXX-XXXX-XX");
  }

  if (!item.item_name || item.item_name.trim() === "") {
    errors.push("Item name is required");
  }

  if (item.initial_stock < 0) {
    errors.push("Initial stock must be 0 or greater");
  }

  return errors;
}

function isRowEmpty(item: InventoryItem): boolean {
  return !item.ndc && !item.item_name;
}

export function EditableInventoryTable({
  onBulkSaveSuccess,
  onCancel,
}: EditableInventoryTableProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [newRows, setNewRows] = React.useState<InventoryItem[]>([]);
  const [validationErrors, setValidationErrors] = React.useState<
    Map<number, string[]>
  >(new Map());
  const [isBulkSaving, setIsBulkSaving] = React.useState(false);
  const [lookupRows, setLookupRows] = React.useState<Set<number>>(new Set());
  const [noMatchRows, setNoMatchRows] = React.useState<Set<number>>(new Set());
  const [lookupSuccessRows, setLookupSuccessRows] = React.useState<Set<number>>(
    new Set()
  );
  const [duplicateNDCRows, setDuplicateNDCRows] = React.useState<Set<number>>(
    new Set()
  );

  const ndcInputRefs = React.useRef<Record<number, HTMLInputElement | null>>(
    {}
  );

  React.useEffect(() => {
    const initialRows: InventoryItem[] = [
      {
        ndc: "",
        item_name: "",
        drug_type: null,
        initial_stock: 0,
        reorder_threshold: 0,
      },
    ];
    setNewRows(initialRows);
    setTimeout(() => {
      ndcInputRefs.current[0]?.focus();
    }, 0);
  }, []);

  const { lookupNDC: fetchNDCData } = useNDCLookup();

  const handleNDCSuccess = React.useCallback(
    (data: NDCLookupResult, index: number) => {
      const { drug_type, item_name, resolvedNDC } = data;
      setNewRows((currentRows) => {
        const updatedRows = [...currentRows];
        updatedRows[index] = {
          ...updatedRows[index],
          ndc: resolvedNDC || updatedRows[index].ndc,
          item_name: item_name,
          drug_type: drug_type || null,
        };
        return updatedRows;
      });
      setLookupSuccessRows((prev) => new Set(prev).add(index));
    },
    []
  );

  const performNDCLookup = React.useCallback(
    async (ndc: string, index: number) => {
      setLookupRows((prev) => new Set(prev).add(index));
      setNoMatchRows((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });

      try {
        const data = await fetchNDCData(ndc);
        if (data) {
          handleNDCSuccess(data, index);
        } else {
          setNoMatchRows((prev) => new Set(prev).add(index));
        }
      } finally {
        setLookupRows((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
    },
    [fetchNDCData, handleNDCSuccess]
  );

  const reindexSetOnInsert = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Set<number>>>,
      insertIndex: number
    ) => {
      setter((prev) => {
        const next = new Set<number>();
        prev.forEach((i) => next.add(i >= insertIndex ? i + 1 : i));
        return next;
      });
    },
    []
  );

  const reindexSetOnDelete = React.useCallback(
    (
      setter: React.Dispatch<React.SetStateAction<Set<number>>>,
      deleteIndex: number
    ) => {
      setter((prev) => {
        const next = new Set<number>();
        prev.forEach((i) => {
          if (i < deleteIndex) next.add(i);
          else if (i > deleteIndex) next.add(i - 1);
        });
        return next;
      });
    },
    []
  );

  const MAX_BULK_ROWS = 10;

  const addNewRowAt = React.useCallback(
    (insertIndex: number) => {
      if (newRows.length >= MAX_BULK_ROWS) return;
      const newRow: InventoryItem = {
        ndc: "",
        item_name: "",
        drug_type: null,
        initial_stock: 0,
        reorder_threshold: 0,
      };
      setNewRows((prev) => {
        const updated = [...prev];
        updated.splice(insertIndex, 0, newRow);
        return updated;
      });
      setValidationErrors((prev) => {
        const reindexed = new Map<number, string[]>();
        prev.forEach((errors, i) => {
          reindexed.set(i >= insertIndex ? i + 1 : i, errors);
        });
        return reindexed;
      });
      reindexSetOnInsert(setLookupRows, insertIndex);
      reindexSetOnInsert(setNoMatchRows, insertIndex);
      reindexSetOnInsert(setLookupSuccessRows, insertIndex);
      reindexSetOnInsert(setDuplicateNDCRows, insertIndex);
      setTimeout(() => {
        ndcInputRefs.current[insertIndex]?.focus();
      }, 50);
    },
    [reindexSetOnInsert, newRows.length]
  );

  const updateRow = (
    index: number,
    field: keyof InventoryItem,
    value: string | number | boolean | null
  ) => {
    const updatedRows = [...newRows];
    updatedRows[index] = { ...updatedRows[index], [field]: value };
    setNewRows(updatedRows);
  };

  const cancelEdit = (index: number) => {
    setNewRows((prev) => prev.filter((_, i) => i !== index));
    setValidationErrors((prev) => {
      const reindexed = new Map<number, string[]>();
      prev.forEach((errors, i) => {
        if (i < index) reindexed.set(i, errors);
        else if (i > index) reindexed.set(i - 1, errors);
      });
      return reindexed;
    });
    reindexSetOnDelete(setLookupRows, index);
    reindexSetOnDelete(setNoMatchRows, index);
    reindexSetOnDelete(setLookupSuccessRows, index);
    reindexSetOnDelete(setDuplicateNDCRows, index);
  };

  const validateAllRows = (): boolean => {
    const errors = new Map<number, string[]>();
    let hasErrors = false;

    newRows.forEach((row, index) => {
      if (!isRowEmpty(row)) {
        const rowErrors = validateInventoryItem(row);
        if (rowErrors.length > 0) {
          errors.set(index, rowErrors);
          hasErrors = true;
        }
      }
    });

    setValidationErrors(errors);
    return !hasErrors;
  };

  const bulkSaveRows = async (approvalToken?: string) => {
    if (newRows.length === 0) return;

    const nonEmptyRows = newRows.filter((row) => !isRowEmpty(row));

    if (nonEmptyRows.length === 0) {
      toast({
        title: "No Data",
        description: "Please fill out at least one row before saving",
        variant: "destructive",
      });
      return;
    }

    if (!approvalToken) {
      const isValid = validateAllRows();
      if (!isValid) {
        const errorCount = validationErrors.size;
        toast({
          title: "Validation Errors",
          description: `Please fix ${errorCount} validation error${errorCount > 1 ? "s" : ""} before saving`,
          variant: "destructive",
        });
        return;
      }
    }

    setIsBulkSaving(true);

    try {
      const result = await bulkCreateInventory({
        items: nonEmptyRows as unknown as Record<string, unknown>[],
        approvalToken,
      });

      toast({
        title: "Success",
        description: `${result.count} drug${result.count > 1 ? "s" : ""} added successfully`,
      });

      setNewRows([]);
      setValidationErrors(new Map());
      setDuplicateNDCRows(new Set());

      onBulkSaveSuccess?.();
    } catch (error) {
      if (error instanceof ApiClientError) {
        if (
          error.statusCode === 403 &&
          handlePermissionError(error, {
            requestApproval,
            toast,
            retryFn: (token) => bulkSaveRows(token),
            actionDescription: "Add controlled substance inventory items",
          })
        )
          return;

        const errorData = error.data as Record<string, unknown>;
        if (errorData.duplicateNDCs && Array.isArray(errorData.duplicateNDCs)) {
          const duplicateSet = new Set(errorData.duplicateNDCs as string[]);
          const duplicateIndexes: number[] = [];
          for (let i = 0; i < newRows.length; i++) {
            if (duplicateSet.has(newRows[i].ndc)) duplicateIndexes.push(i);
          }
          setDuplicateNDCRows(new Set(duplicateIndexes));
        } else if (errorData.validationErrors) {
          const errorMap = new Map<number, string[]>();
          (
            errorData.validationErrors as { index: number; message: string }[]
          ).forEach((err) => {
            const existing = errorMap.get(err.index) || [];
            existing.push(err.message);
            errorMap.set(err.index, existing);
          });
          setValidationErrors(errorMap);
        }

        toast({
          title: "Error",
          description: error.message || "Failed to create inventory items",
          variant: "destructive",
        });
        return;
      }

      console.error("Bulk save error:", error);
      toast({
        title: "Error",
        description: "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsBulkSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
            <Pill className="h-4.5 w-4.5 text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold leading-tight">Add Drugs</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Enter an NDC and press Enter to auto-fill drug details
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onCancel && (
            <Button
              onClick={onCancel}
              size="sm"
              variant="outline"
              disabled={isBulkSaving}
            >
              Cancel
            </Button>
          )}
          {newRows.length > 0 && (
            <Button
              onClick={() => bulkSaveRows()}
              size="sm"
              disabled={isBulkSaving}
            >
              {isBulkSaving ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              Save All
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-surface overflow-hidden">
        <div className="overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[280px]">
                  <div className="flex items-center gap-1.5">
                    <span>Drug / NDC</span>
                    <FieldHint>
                      The 10 or 11 digit National Drug Code. Type it and C2
                      Vault will auto fill the drug name and details from the
                      federal database.
                    </FieldHint>
                  </div>
                </TableHead>
                <TableHead className="w-[130px]">
                  <div className="flex items-center gap-1.5">
                    <span>Schedule</span>
                    <FieldHint>
                      DEA schedule (CII through CV) based on abuse potential.
                      Schedule II items are tracked under the strictest rules.
                    </FieldHint>
                  </div>
                </TableHead>
                <TableHead className="w-[110px]">
                  <div className="flex items-center gap-1.5">
                    <span>Initial Stock</span>
                    <FieldHint>
                      How much you have on hand right now. This is the starting
                      point for the audit trail. Receipts and dispenses adjust
                      from here.
                    </FieldHint>
                  </div>
                </TableHead>
                <TableHead className="w-[120px]">
                  <div className="flex items-center gap-1.5">
                    <span>Min. Stock Level</span>
                    <FieldHint>
                      The lowest amount you want to keep in stock. Once stock
                      drops to or below this level, the item shows a low stock
                      alert.
                    </FieldHint>
                  </div>
                </TableHead>
                <TableHead className="w-[52px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {newRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted/60">
                        <Pill className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          No drugs added yet
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Click below to add your first drug
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addNewRowAt(0)}
                        className="mt-1"
                      >
                        <Plus className="h-3.5 w-3.5 mr-1.5" />
                        Add drug
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {newRows.map((row, index) => {
                    const rowErrors = validationErrors.get(index) || [];
                    const hasErrors = rowErrors.length > 0;
                    const isDuplicate = duplicateNDCRows.has(index);
                    return (
                      <React.Fragment key={`new-${index}`}>
                        <TableRow
                          className={
                            isDuplicate
                              ? "border-l-2 border-l-amber-500 bg-amber-50/50"
                              : hasErrors
                                ? "border-l-2 border-l-destructive"
                                : ""
                          }
                        >
                          {/* Drug / NDC */}
                          <TableCell>
                            <div className="space-y-1.5">
                              <div className="relative">
                                <Input
                                  ref={(el) => {
                                    ndcInputRefs.current[index] = el;
                                  }}
                                  value={row.ndc}
                                  onChange={(e) => {
                                    const rawDigits = sanitizeNDCInput(
                                      e.target.value
                                    );
                                    updateRow(index, "ndc", rawDigits);

                                    if (hasErrors) {
                                      const newErrors = new Map(
                                        validationErrors
                                      );
                                      newErrors.delete(index);
                                      setValidationErrors(newErrors);
                                    }

                                    setNoMatchRows((prev) => {
                                      const next = new Set(prev);
                                      next.delete(index);
                                      return next;
                                    });

                                    setLookupSuccessRows((prev) => {
                                      const next = new Set(prev);
                                      next.delete(index);
                                      return next;
                                    });

                                    setDuplicateNDCRows((prev) => {
                                      const next = new Set(prev);
                                      next.delete(index);
                                      return next;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "Enter" &&
                                      isNDCLookupLength(e.currentTarget.value)
                                    ) {
                                      e.preventDefault();
                                      const digits =
                                        e.currentTarget.value.replace(
                                          /\D/g,
                                          ""
                                        );
                                      performNDCLookup(digits, index);
                                    }
                                  }}
                                  onBlur={() => {
                                    const digits = row.ndc.replace(/\D/g, "");
                                    if (digits.length === 11) {
                                      updateRow(
                                        index,
                                        "ndc",
                                        formatNDC(digits)
                                      );
                                    }
                                    if (isNDCLookupLength(digits)) {
                                      performNDCLookup(digits, index);
                                    }
                                  }}
                                  placeholder="NDC code"
                                  className={`h-8 font-mono text-sm ${isDuplicate ? "border-amber-500 focus-visible:ring-amber-500" : hasErrors && rowErrors.some((e) => e.includes("NDC")) ? "border-destructive" : ""}`}
                                  disabled={isBulkSaving}
                                />
                                {lookupRows.has(index) && (
                                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                              {hasErrors &&
                                rowErrors.some((e) => e.includes("NDC")) && (
                                  <p className="text-xs text-destructive">
                                    {rowErrors.find((e) => e.includes("NDC"))}
                                  </p>
                                )}
                              {isDuplicate && (
                                <p className="text-xs text-amber-600 font-medium">
                                  This NDC already exists in your inventory
                                </p>
                              )}
                              {!hasErrors &&
                                !isDuplicate &&
                                noMatchRows.has(index) && (
                                  <p className="text-xs text-muted-foreground">
                                    No match found — you can still add this drug
                                    manually
                                  </p>
                                )}
                              <Input
                                value={row.item_name}
                                onChange={(e) => {
                                  updateRow(index, "item_name", e.target.value);
                                  if (hasErrors) {
                                    const newErrors = new Map(validationErrors);
                                    newErrors.delete(index);
                                    setValidationErrors(newErrors);
                                  }
                                }}
                                placeholder="Drug name"
                                className={`h-8 text-sm ${hasErrors && rowErrors.some((e) => e.includes("Item name")) ? "border-destructive" : ""}`}
                                disabled={isBulkSaving}
                              />
                              {hasErrors &&
                                rowErrors.some((e) =>
                                  e.includes("Item name")
                                ) && (
                                  <p className="text-xs text-destructive">
                                    {rowErrors.find((e) =>
                                      e.includes("Item name")
                                    )}
                                  </p>
                                )}
                              {lookupSuccessRows.has(index) && (
                                <p className="text-xs text-muted-foreground">
                                  Verify name, strength, and dosage form before
                                  saving.
                                </p>
                              )}
                            </div>
                          </TableCell>

                          {/* Schedule */}
                          <TableCell>
                            {noMatchRows.has(index) ? (
                              <Select
                                value={row.drug_type || "non_controlled"}
                                onValueChange={(value) =>
                                  updateRow(
                                    index,
                                    "drug_type",
                                    value === "non_controlled" ? null : value
                                  )
                                }
                                disabled={isBulkSaving}
                              >
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="non_controlled">
                                    Non-Controlled
                                  </SelectItem>
                                  <SelectItem value="schedule_ii">
                                    Schedule II
                                  </SelectItem>
                                  <SelectItem value="schedule_iii">
                                    Schedule III
                                  </SelectItem>
                                  <SelectItem value="schedule_iv">
                                    Schedule IV
                                  </SelectItem>
                                  <SelectItem value="schedule_v">
                                    Schedule V
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-sm text-muted-foreground">
                                {formatDrugSchedule(row.drug_type)}
                              </span>
                            )}
                          </TableCell>

                          {/* Initial Stock */}
                          <TableCell>
                            <div className="space-y-1">
                              <Input
                                type="number"
                                min={0}
                                value={row.initial_stock}
                                onChange={(e) => {
                                  updateRow(
                                    index,
                                    "initial_stock",
                                    parseInt(e.target.value) || 0
                                  );
                                  if (hasErrors) {
                                    const newErrors = new Map(validationErrors);
                                    newErrors.delete(index);
                                    setValidationErrors(newErrors);
                                  }
                                }}
                                className={`h-8 ${hasErrors && rowErrors.some((e) => e.includes("stock")) ? "border-destructive" : ""}`}
                                disabled={isBulkSaving}
                              />
                              {hasErrors &&
                                rowErrors.some((e) => e.includes("stock")) && (
                                  <p className="text-xs text-destructive">
                                    {rowErrors.find((e) => e.includes("stock"))}
                                  </p>
                                )}
                            </div>
                          </TableCell>

                          {/* Min Stock Level */}
                          <TableCell>
                            <Input
                              type="number"
                              value={row.reorder_threshold}
                              onChange={(e) =>
                                updateRow(
                                  index,
                                  "reorder_threshold",
                                  parseInt(e.target.value) || 0
                                )
                              }
                              className="h-8"
                              disabled={isBulkSaving}
                            />
                          </TableCell>

                          {/* Delete */}
                          <TableCell className="text-right pr-3">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => cancelEdit(index)}
                              disabled={isBulkSaving}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {/* Insert-below divider */}
                        <tr className="group/insert border-0">
                          <td colSpan={5} className="p-0 border-0 h-0">
                            <div className="relative flex items-center justify-center h-0 overflow-visible">
                              <button
                                type="button"
                                onClick={() => addNewRowAt(index + 1)}
                                disabled={
                                  isBulkSaving ||
                                  newRows.length >= MAX_BULK_ROWS
                                }
                                className="relative z-10 flex items-center gap-1 -translate-y-1/2 opacity-0 group-hover/insert:opacity-100 focus:opacity-100 transition-opacity text-xs text-muted-foreground hover:text-primary bg-background border border-dashed border-border hover:border-primary/60 rounded px-2 py-0.5 leading-none"
                              >
                                <Plus className="h-3 w-3" />
                                Add drug
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}

                  {/* Bottom add-drug row */}
                  <TableRow className="border-0 hover:bg-transparent">
                    <TableCell colSpan={5} className="py-3 px-4">
                      <button
                        type="button"
                        onClick={() => addNewRowAt(newRows.length)}
                        disabled={
                          isBulkSaving || newRows.length >= MAX_BULK_ROWS
                        }
                        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border py-2 text-sm text-muted-foreground transition-colors hover:border-primary/60 hover:bg-primary-soft/50 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                      >
                        <Plus className="h-4 w-4" />
                        {newRows.length >= MAX_BULK_ROWS
                          ? `Maximum of ${MAX_BULK_ROWS} drugs reached`
                          : "Add another drug"}
                      </button>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
